// Day insights: for every trading day, each mistake the trades show (from behaviorDetect.js, per
// day), plus day-level checks (an overtrading day, a loss much bigger than usual, a day over the
// account's consistency limit, a day not journaled). Each insight says what it cost, how often it
// has happened lately, and the rule that would stop it. A day with no mistakes is a clean day, and
// a clean day says which habits have stopped. The journal's job is to call out every mistake until
// it stops.
import { PATTERNS, detectBehavior, ruleFor } from './behaviorDetect';
import { formatMoney, netPnl, tradingDayKey } from './pnl';
import { translateNow as tn } from './langState';

// How serious each insight is: 'cost' (a costly habit), 'watch', 'info' (good to know), 'clean'.
const INFO_KINDS = new Set(['offHours', 'cutWinners']);
const LEVELS = ['cost', 'watch', 'clean', 'info'];
export const REPEAT_WINDOW = 10;

const ordinal = (n) => (n === 1 ? tn('1st') : n === 2 ? tn('2nd') : n === 3 ? tn('3rd') : tn('{n}th', { n }));
const journaled = (n) => Boolean(n && (n.score || (n.review && n.review.trim()) || (n.eveningDone && n.eveningDone.length)));

export function dayInsights(trades, { accounts = [], notes = {} } = {}) {
  const out = {};
  if (!trades.length) return out;
  const found = detectBehavior(trades, { accounts, notes });
  const byDay = {};
  for (const t of trades) (byDay[tradingDayKey(t.date)] = byDay[tradingDayKey(t.date)] || []).push(t);
  const dayKeys = Object.keys(byDay).sort();
  const losses = trades.map(netPnl).filter((n) => n < 0);
  const avgLoss = losses.length ? losses.reduce((s, n) => s + n, 0) / losses.length : 0;
  const names = Object.fromEntries(PATTERNS.map((p) => [p.kind, p.name]));

  // Each account's total profit, for the consistency check.
  const consistency = accounts.filter((a) => a.consistencyLimit).map((a) => {
    const mine = trades.filter((t) => String(t.accountId) === String(a._id));
    const total = mine.reduce((s, t) => s + netPnl(t), 0);
    const days = {};
    for (const t of mine) days[tradingDayKey(t.date)] = (days[tradingDayKey(t.date)] || 0) + netPnl(t);
    return { account: a, total, days };
  });

  // Pass 1: the mistakes on each day.
  for (const day of dayKeys) {
    const list = byDay[day];
    const items = [];
    const kinds = {};
    for (const t of list) {
      for (const f of found.flags.get(t._id) || []) (kinds[f.kind] = kinds[f.kind] || { trades: [], text: f.text }).trades.push(t);
    }
    for (const p of PATTERNS) {
      const k = kinds[p.kind];
      if (!k) continue;
      const net = k.trades.reduce((s, t) => s + netPnl(t), 0);
      const level = INFO_KINDS.has(p.kind) && net >= 0 ? 'info' : p.costless || net >= 0 ? 'watch' : 'cost';
      items.push({
        kind: p.kind, level, title: tn(p.name), text: k.text, count: k.trades.length,
        net, cost: p.costless ? null : net, rule: ruleFor(p.kind, found.baseline), tradeIds: k.trades.map((t) => t._id),
      });
    }
    // A loss much bigger than usual.
    const worst = Math.min(...list.map(netPnl));
    if (avgLoss < 0 && worst < 0 && worst <= avgLoss * 2) {
      items.push({ kind: 'bigLoss', level: 'watch', title: tn('Loss much bigger than usual'), net: worst, cost: worst,
        text: tn('Your biggest loss today was {amount}, {x}× your average loss.', { amount: formatMoney(worst), x: (worst / avgLoss).toFixed(1) }),
        rule: tn('Every trade has a stop, and I never move it further away.') });
    }
    // An account's day over its consistency limit.
    for (const c of consistency) {
      const dayNet = c.days[day];
      if (dayNet > 0 && c.total > 0 && (dayNet / c.total) * 100 > c.account.consistencyLimit) {
        items.push({ kind: 'consistency', level: 'watch', title: tn('Over your consistency limit'), net: dayNet, cost: null,
          text: tn('This day is {p}% of your profit on {name}; the rule is {limit}% max.', { p: Math.round((dayNet / c.total) * 100), name: c.account.name, limit: c.account.consistencyLimit }),
          rule: tn('No single day makes more than {limit}% of my profit.', { limit: c.account.consistencyLimit }) });
      }
    }
    // Not journaled (only for days before today, and only once there's a habit of journaling to keep).
    if (day < tradingDayKey(new Date()) && !journaled(notes[day])) {
      items.push({ kind: 'notJournaled', level: 'info', title: tn('Day not journaled'), net: null, cost: null,
        text: tn('No review written for this day. The lessons are in the days you look back on.'), rule: tn('I write a short review after every trading day.') });
    }
    out[day] = { day, items, trades: list.length, net: list.reduce((s, t) => s + netPnl(t), 0) };
  }

  // Pass 2: how often each mistake has come back, and what a clean day has stopped.
  dayKeys.forEach((day, i) => {
    const recent = dayKeys.slice(Math.max(0, i - REPEAT_WINDOW + 1), i + 1);
    const entry = out[day];
    for (const it of entry.items) {
      if (it.kind === 'notJournaled') continue;
      const times = recent.filter((d) => out[d].items.some((x) => x.kind === it.kind)).length;
      if (times > 1) it.repeat = tn('{nth} time in your last {n} trading days', { nth: ordinal(times), n: recent.length });
      it.times = times;
    }
    const mistakes = entry.items.filter((it) => it.level === 'cost' || it.level === 'watch');
    if (!mistakes.length) {
      // Habits seen before that haven't come back: the longest run without each.
      const stopped = [];
      for (const kind of [...Object.keys(names), 'bigLoss']) {
        const last = dayKeys.slice(0, i).reverse().findIndex((d) => out[d].items.some((x) => x.kind === kind && x.level !== 'info'));
        if (last >= 2) stopped.push({ kind, days: last + 1 });
      }
      stopped.sort((a, b) => b.days - a.days);
      entry.clean = true;
      entry.items.unshift({
        kind: 'clean', level: 'clean', title: tn('Clean day'), net: entry.net, cost: null,
        text: stopped.length
          ? tn('No costly habits today. {n} trading days in a row now without {habit}.', { n: stopped[0].days, habit: (names[stopped[0].kind] ? tn(names[stopped[0].kind]) : tn('a loss much bigger than usual')).toLowerCase() })
          : tn('No costly habits today: no revenge trades, no size-ups, no trading past your limits. This is the day to copy.'),
      });
    }
    entry.items.sort((a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level));
    entry.level = entry.items.length ? entry.items[0].level : null;
    // The badge counts mistakes (costly and watch); good-to-know notes are only in the card.
    entry.count = entry.items.filter((it) => it.level === 'cost' || it.level === 'watch').length;
    // The trader marked the day's mistakes as reviewed: they still count everywhere else.
    entry.reviewed = Boolean(notes[day] && notes[day].insightsReviewedAt);
  });
  return out;
}
